import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { formatFitpassMonth } from "@/utils/fitpassMonth";
import { formatDate } from "@/lib/formatDate";
import { AdditionalPerson, FitpassRequest } from "@/types";

interface FitpassRequestsTableProps {
  isAdmin: boolean;
  isLoading: boolean;
  requests: FitpassRequest[];
  selectedMonth: string;
  filterMonthOptions: string[];
  isViewingCurrentMonth: boolean;
  nextMonthLabel: string;
  isCopying: boolean;
  canEditPayment: (employeeId: string) => boolean;
  onMonthChange: (month: string) => void;
  onCopyClick: () => void;
  onEdit: (request: FitpassRequest) => void;
  onDelete: (request: FitpassRequest) => void;
  onPaidToggle: (requestId: string, currentPaidStatus: boolean) => void;
  onAdditionalPersonPaidToggle: (
    requestId: string,
    personIndex: number,
    currentPaidStatus: boolean,
  ) => void;
}

const FitpassRequestsTable: React.FC<FitpassRequestsTableProps> = ({
  isAdmin,
  isLoading,
  requests,
  selectedMonth,
  filterMonthOptions,
  isViewingCurrentMonth,
  nextMonthLabel,
  isCopying,
  canEditPayment,
  onMonthChange,
  onCopyClick,
  onEdit,
  onDelete,
  onPaidToggle,
  onAdditionalPersonPaidToggle,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (requestId: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {isAdmin ? "All Fitpass Requests" : "My Fitpass Requests"}
          </Typography>

          {isAdmin && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isViewingCurrentMonth && (
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={onCopyClick}
                  disabled={isCopying || requests.length === 0}
                >
                  Copy to {nextMonthLabel}
                </Button>
              )}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Filter by Month"
                  onChange={(e) => onMonthChange(e.target.value)}
                >
                  {filterMonthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} height={60} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell width="50px" />
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Additional People</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Submitted</TableCell>
                  {isAdmin && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                      <Typography color="text.secondary">
                        {!isAdmin
                          ? "You don't have a Fitpass membership yet. Please contact an admin."
                          : `No requests for ${selectedMonth}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request, index) => {
                    const hasAdditionalPeople =
                      request.additionalPeople &&
                      Array.isArray(request.additionalPeople) &&
                      request.additionalPeople.length > 0;
                    const isExpanded = expandedRows.has(request.id);

                    return (
                      <React.Fragment key={request.id}>
                        <TableRow hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {hasAdditionalPeople && (
                              <IconButton
                                size="small"
                                onClick={() => toggleRow(request.id)}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            {request.employeeName} {request.employeeSurname}
                          </TableCell>
                          <TableCell>{request.employeeEmail}</TableCell>
                          <TableCell>
                            {hasAdditionalPeople ? (
                              <Typography variant="body2" color="primary">
                                {request.additionalPeople!.length}{" "}
                                {request.additionalPeople!.length === 1
                                  ? "person"
                                  : "people"}
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {formatFitpassMonth(request.month)}
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={request.paid || false}
                              onChange={() =>
                                onPaidToggle(request.id, request.paid || false)
                              }
                              color="success"
                              disabled={!canEditPayment(request.employeeId)}
                            />
                          </TableCell>
                          <TableCell>{formatDate(request.createdAt)}</TableCell>
                          {isAdmin && (
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => onEdit(request)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDelete(request)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>

                        {hasAdditionalPeople &&
                          isExpanded &&
                          request.additionalPeople!.map(
                            (person: AdditionalPerson, personIndex: number) => (
                              <TableRow
                                key={`${request.id}-person-${personIndex}`}
                                sx={{ bgcolor: "action.hover" }}
                              >
                                <TableCell />
                                <TableCell />
                                <TableCell sx={{ pl: 6 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {person.name} {person.surname}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {person.email}
                                  </Typography>
                                </TableCell>
                                <TableCell />
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {formatFitpassMonth(request.month)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={person.paid || false}
                                    onChange={() =>
                                      onAdditionalPersonPaidToggle(
                                        request.id,
                                        personIndex,
                                        person.paid || false,
                                      )
                                    }
                                    color="success"
                                    size="small"
                                    disabled={
                                      !canEditPayment(request.employeeId)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {formatDate(request.createdAt)}
                                  </Typography>
                                </TableCell>
                                {isAdmin && <TableCell />}
                              </TableRow>
                            ),
                          )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default FitpassRequestsTable;
